import { useState, useMemo, useEffect, useRef, useCallback } from "react";
import { partnerBrands, brandCategories, type BrandCategory } from "@/mocks/brands";
import { Link } from "react-router-dom";
import usePageSEO from "@/hooks/usePageSEO";
import JsonLd from "@/components/feature/JsonLd";

const siteUrl = import.meta.env.VITE_SITE_URL || "https://ghbeauty.kr";

const breadcrumbSchema = {
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  "itemListElement": [
    { "@type": "ListItem", "position": 1, "name": "Home", "item": siteUrl + "/" },
    { "@type": "ListItem", "position": 2, "name": "Brands", "item": siteUrl + "/brands" }
  ]
};

const largeBrandNames = new Set([
  "ANUA", "Arencia", "AXIS-Y", "BANILA CO", "dasique", "ELROEL",
  "EQQUALBERRY", "ETUDE", "HYGGEE", "JMsolution", "ma:nyo",
  "MENOKIN", "SKIN1004", "SOME BY MI",
]);

const categoryGradients: Record<string, string> = {
  "Core Skincare": "from-amber-100/60 via-amber-50/40 to-transparent",
  "Derma & Active Care": "from-teal-100/50 via-teal-50/30 to-transparent",
  "Makeup & Beauty": "from-rose-100/60 via-rose-50/40 to-transparent",
  "Hair & Body Care": "from-stone-100/50 via-stone-50/30 to-transparent",
  "Perfume & Fragrance": "from-emerald-100/50 via-emerald-50/30 to-transparent",
  "Trending & Discovery": "from-orange-100/50 via-orange-50/30 to-transparent",
  "For Men": "from-slate-100/50 via-slate-50/30 to-transparent",
  "Kid & Baby": "from-pink-100/50 via-pink-50/30 to-transparent",
};

const getMonogram = (name: string) => {
  const clean = name.replace(/[^A-Za-z0-9]/g, "");
  return clean.slice(0, 2).toUpperCase();
};

export default function BrandsPage() {
  usePageSEO({
    title: "Korean Beauty Brands Wholesale | GH Corporation",
  });

  const [activeCategory, setActiveCategory] = useState<BrandCategory>("All");
  const [visibleCount, setVisibleCount] = useState(24);
  const [selectedBrand, setSelectedBrand] = useState<string | null>(null);
  const [popoverPos, setPopoverPos] = useState<{ top: number; left: number; arrowLeft: boolean } | null>(null);
  const popoverRef = useRef<HTMLDivElement>(null);

  const filteredBrands = useMemo(() => {
    if (activeCategory === "All") return partnerBrands;
    return partnerBrands.filter((b) => b.category === activeCategory);
  }, [activeCategory]);

  const visibleBrands = useMemo(() => {
    return filteredBrands.slice(0, visibleCount);
  }, [filteredBrands, visibleCount]);

  const hasMore = visibleCount < filteredBrands.length;

  const closePopover = useCallback(() => {
    setSelectedBrand(null);
    setPopoverPos(null);
  }, []);

  const handleBrandClick = useCallback((brandName: string, e: React.MouseEvent) => {
    e.stopPropagation();
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const popoverWidth = 280;
    const gap = 14;

    // Determine if popover should appear on the left (if card is too close to right edge)
    const wouldOverflowRight = rect.right + gap + popoverWidth > viewportWidth;
    const arrowLeft = !wouldOverflowRight;

    setPopoverPos({
      top: rect.top + rect.height / 2, // fixed positioning uses viewport coords
      left: wouldOverflowRight ? rect.left - gap - popoverWidth : rect.right + gap,
      arrowLeft,
    });
    setSelectedBrand(brandName);
  }, []);

  // Close popover on outside click
  useEffect(() => {
    if (!selectedBrand) return;

    const handleClickOutside = (e: MouseEvent) => {
      if (popoverRef.current && !popoverRef.current.contains(e.target as Node)) {
        closePopover();
      }
    };

    // Small delay to avoid the same click closing it immediately
    const timer = setTimeout(() => {
      document.addEventListener("mousedown", handleClickOutside);
    }, 0);

    return () => {
      clearTimeout(timer);
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [selectedBrand, closePopover]);

  // Close popover on Escape key
  useEffect(() => {
    if (!selectedBrand) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") closePopover();
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [selectedBrand, closePopover]);

  // Close popover on scroll
  useEffect(() => {
    if (!selectedBrand) return;
    window.addEventListener("scroll", closePopover, { passive: true });
    return () => window.removeEventListener("scroll", closePopover);
  }, [selectedBrand, closePopover]);

  return (
    <section className="w-full px-6 md:px-12 lg:px-20 pt-28 pb-24 md:pb-32 bg-background-100">
      <JsonLd data={breadcrumbSchema} />
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
          <div className="lg:col-span-7">
            <div className="flex items-center gap-3 mb-6">
              <span className="w-8 h-px bg-accent-500" />
              <span className="font-label text-[11px] tracking-[0.22em] uppercase text-accent-700">
                Brand Portfolio
              </span>
            </div>
            <h1 className="font-heading text-foreground-950 text-4xl md:text-5xl lg:text-6xl leading-[1.05] font-light">
              125+ Korean Beauty Brands.
              <br />
              <span className="italic">One Trusted Export Partner.</span>
            </h1>
          </div>
          <div className="lg:col-span-5 lg:pl-8">
            <p className="text-foreground-700 text-[15px] md:text-base leading-relaxed font-light">
              GH Corporation provides overseas buyers with access to a broad portfolio of leading Korean beauty brands. Combine multiple brands and product categories into one consolidated order, supported by Korean sourcing, inspection, export packing and international shipping.
            </p>
          </div>
        </div>

        {/* Category Filter Tabs */}
        <div className="mt-12 flex flex-wrap gap-2">
          {brandCategories.map((cat) => {
            const count =
              cat === "All"
                ? partnerBrands.length
                : partnerBrands.filter((b) => b.category === cat).length;
            return (
              <button
                key={cat}
                onClick={() => {
                  setActiveCategory(cat);
                  setVisibleCount(24);
                }}
                className={`whitespace-nowrap cursor-pointer px-4 py-2 rounded-full text-[13px] font-label tracking-[0.06em] transition-all duration-300 ${
                  activeCategory === cat
                    ? "bg-primary-500 text-background-50"
                    : "bg-background-50 text-foreground-700 border border-background-200/70 hover:border-accent-300 hover:text-accent-700"
                }`}
              >
                {cat === "All" ? "All Brands" : cat}
                <span className="ml-1.5 text-[11px] opacity-80">({count})</span>
              </button>
            );
          })}
        </div>

        {/* Brand Cards Grid */}
        <div className="mt-10 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5" data-product-shop>
          {visibleBrands.map((b) => (
            <article
              key={b.name}
              onClick={(e) => handleBrandClick(b.name, e)}
              className={`group relative rounded-2xl overflow-hidden bg-background-50 border transition-all duration-300 cursor-pointer ${
                selectedBrand === b.name
                  ? "border-accent-500 ring-2 ring-accent-200"
                  : "border-background-200/70 hover:border-accent-300"
              }`}
            >
              {/* Subtle gradient accent */}
              <div
                className={`absolute inset-0 bg-gradient-to-br ${
                  categoryGradients[b.category] || "from-background-100/40 via-background-50/20 to-transparent"
                } opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none`}
              />

              <div className="relative p-5 md:p-6 flex flex-col min-h-[220px] md:min-h-[260px] justify-between items-center text-center">
                {/* Logo or brand monogram */}
                {b.logo ? (
                  <div className="flex-1 flex items-center justify-center w-full py-2">
                    <img
                      src={b.logo}
                      alt={`${b.name} brand logo`}
                      className={`object-contain ${
                        largeBrandNames.has(b.name)
                          ? "max-w-[260px] max-h-[160px] md:max-h-[200px]"
                          : "max-w-[200px] max-h-[120px] md:max-h-[160px]"
                      }`}
                    />
                  </div>
                ) : (
                  <div className="flex-1 flex items-center justify-center w-full py-2">
                    <span className="w-16 h-16 md:w-20 md:h-20 rounded-full flex items-center justify-center bg-background-100 text-foreground-500 font-heading text-lg md:text-xl tracking-[0.05em] group-hover:bg-accent-100 group-hover:text-accent-700 transition-colors duration-300">
                      {getMonogram(b.name)}
                    </span>
                  </div>
                )}

                {/* Brand name */}
                <div className={b.logo ? "mt-3" : ""}>
                  <h4 className="font-heading text-foreground-950 text-lg md:text-xl leading-tight group-hover:text-accent-700 transition-colors duration-300">
                    {b.name}
                  </h4>
                  <p className="mt-1.5 font-label text-[10px] tracking-[0.16em] uppercase text-foreground-500 group-hover:text-foreground-700 transition-colors duration-300">
                    {b.category}
                  </p>
                </div>
              </div>
            </article>
          ))}
        </div>

        {/* Popover */}
        {selectedBrand && popoverPos && (
          <div
            ref={popoverRef}
            className="fixed z-[100] animate-popover-in"
            style={{
              top: popoverPos.top,
              left: popoverPos.left,
              transform: "translateY(-50%)",
            }}
          >
            {/* Arrow */}
            {popoverPos.arrowLeft ? (
              <div className="absolute top-1/2 -left-[7px] -translate-y-1/2 w-0 h-0 border-t-[7px] border-t-transparent border-b-[7px] border-b-transparent border-r-[7px] border-r-background-50 drop-shadow-[0_0_2px_rgba(0,0,0,0.06)]" />
            ) : (
              <div className="absolute top-1/2 -right-[7px] -translate-y-1/2 w-0 h-0 border-t-[7px] border-t-transparent border-b-[7px] border-b-transparent border-l-[7px] border-l-background-50 drop-shadow-[0_0_2px_rgba(0,0,0,0.06)]" />
            )}

            <div className="bg-background-50 border border-background-200/70 rounded-2xl p-5 shadow-[0_8px_30px_rgba(0,0,0,0.08)] min-w-[260px]">
              {/* Close button */}
              <button
                onClick={closePopover}
                className="absolute top-3 right-3 w-7 h-7 flex items-center justify-center rounded-full text-foreground-400 hover:text-foreground-700 hover:bg-background-100 transition-colors cursor-pointer"
              >
                <i className="ri-close-line text-sm" />
              </button>

              <div className="flex items-start gap-3 pr-5">
                <div className="w-9 h-9 flex items-center justify-center rounded-full bg-accent-100 text-accent-700 shrink-0 mt-0.5">
                  <i className="ri-question-answer-line text-lg w-5 h-5 flex items-center justify-center" />
                </div>
                <div>
                  <h4 className="font-heading text-foreground-950 text-sm leading-snug">
                    {selectedBrand}
                  </h4>
                  <p className="mt-2 text-foreground-600 text-[13px] leading-relaxed">
                    Interested in this brand? Send us an inquiry and we'll get back to you with details.
                  </p>
                  <Link
                    to="/contact"
                    onClick={closePopover}
                    className="whitespace-nowrap cursor-pointer mt-3 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors font-label text-[12px] tracking-[0.08em]"
                  >
                    Send Inquiry
                    <i className="ri-arrow-right-line text-sm w-4 h-4 flex items-center justify-center" />
                  </Link>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Load More */}
        {hasMore && (
          <div className="mt-10 flex justify-center">
            <button
              onClick={() => setVisibleCount((prev) => prev + 24)}
              className="whitespace-nowrap cursor-pointer inline-flex items-center gap-2 px-6 py-3 rounded-full bg-background-50 text-foreground-700 border border-background-200/70 hover:border-accent-300 hover:text-accent-700 transition-all duration-300 font-label text-[12px] tracking-[0.12em] uppercase"
            >
              View More Featured Brands
              <i className="ri-arrow-down-line" />
            </button>
          </div>
        )}

        {/* Brand availability disclaimer */}
        <p className="mt-8 text-center text-foreground-500 text-[12px] leading-relaxed max-w-3xl mx-auto">
          Brand availability, pricing and export eligibility may vary by territory, sales channel, order volume and individual brand policy.
        </p>

        {/* Product Categories */}
        <div className="mt-16">
          <div className="flex items-center justify-center gap-3 mb-6">
            <span className="w-8 h-px bg-accent-500" />
            <span className="font-label text-[11px] tracking-[0.22em] uppercase text-accent-700">
              Product Categories
            </span>
            <span className="w-8 h-px bg-accent-500" />
          </div>
          <h3 className="text-center font-heading text-foreground-950 text-3xl md:text-4xl leading-tight">
            Product Categories
          </h3>
          <p className="mt-4 max-w-2xl mx-auto text-center text-foreground-700 text-[15px] leading-relaxed font-light">
            Build a market-ready K-beauty assortment across skincare, derma, sun care, masks, makeup, hair and body categories through one consolidated Korean supply partner.
          </p>

          <div className="mt-8 grid grid-cols-2 sm:grid-cols-4 gap-3 md:gap-4">
            {[
              { icon: "ri-drop-line", name: "Skincare" },
              { icon: "ri-heart-pulse-line", name: "Derma Skincare" },
              { icon: "ri-sun-line", name: "Sun Care" },
              { icon: "ri-emotion-line", name: "Sheet Masks" },
              { icon: "ri-palette-line", name: "Color Cosmetics" },
              { icon: "ri-contrast-drop-2-line", name: "Hair Care" },
              { icon: "ri-hand-sanitizer-line", name: "Body Care" },
              { icon: "ri-men-line", name: "Men's Grooming" },
            ].map((cat) => (
              <div
                key={cat.name}
                className="group flex items-center gap-4 p-5 rounded-2xl bg-background-50 border border-background-200/70 hover:border-accent-300 transition-all cursor-pointer"
              >
                <span className="w-11 h-11 flex items-center justify-center rounded-xl bg-accent-100 text-accent-700 group-hover:bg-accent-500 group-hover:text-background-50 transition-colors shrink-0">
                  <i className={`${cat.icon} text-xl w-5 h-5 flex items-center justify-center`} />
                </span>
                <span className="font-heading text-foreground-950 text-base md:text-lg leading-tight group-hover:text-accent-700 transition-colors">
                  {cat.name}
                </span>
              </div>
            ))}
          </div>

          <div className="mt-8 flex justify-center">
            <Link
              to="/contact"
              className="whitespace-nowrap cursor-pointer inline-flex items-center gap-3 px-6 py-3.5 rounded-full bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors font-label text-[13px] tracking-[0.16em] uppercase"
            >
              Request Brand &amp; Product List
              <i className="ri-arrow-right-up-line" />
            </Link>
          </div>
        </div>

        {/* Additional brands note */}
        <p className="mt-10 text-center text-foreground-500 text-[13px] leading-relaxed font-light">
          Additional brands are available upon request. Contact us for our latest brand portfolio, product availability and wholesale quotation.
        </p>

        {/* CTA Banner */}
        <div className="mt-14 flex flex-col md:flex-row md:items-center md:justify-between gap-5 p-6 md:p-8 rounded-2xl bg-background-50 border border-background-200/70">
          <p className="text-foreground-700 text-[15px] md:text-base">
            Contact us for our complete brand and wholesale product list.
          </p>
          <Link
            to="/contact"
            className="whitespace-nowrap cursor-pointer inline-flex items-center gap-3 px-5 py-3 rounded-full bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors font-label text-[12px] tracking-[0.18em] uppercase self-start md:self-auto"
          >
            Request Full Brand List
            <i className="ri-arrow-right-up-line" />
          </Link>
        </div>
      </div>
    </section>
  );
}